import React, { useRef, useState, useEffect } from 'react';
import { QrCode } from 'lucide-react';
import jsQR from 'jsqr'; 
const QrScanner = ({ navigateTo, photos }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Toggle QR scanner
  const toggleScanner = () => {
    if (isScanning) {
      stopScanner();
    } else {
      startScanner();
    }
  };

  const startScanner = async () => {
    try {
      // Verifica che il browser supporti getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setErrorMessage(
          'Il tuo browser non supporta l\'accesso alla fotocamera. Prova con un browser più recente.'
        );
        return;
      }
      setIsScanning(true);

      // Richiedi l'accesso alla fotocamera
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }, // Usa la fotocamera posteriore
      });
  
      if (videoRef.current) {
        // Assegna il flusso video all'elemento <video>
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play(); // Avvia la riproduzione del video
        };
  
        scanQRCode(); // Avvia la scansione
      }
    } catch (err) {
      console.error('Errore durante l\'accesso alla fotocamera:', err);
      setErrorMessage(
        'Non è stato possibile accedere alla fotocamera. Controlla le autorizzazioni del browser.'
      );
    }
  };
  
  const stopScanner = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      // Interrompi tutti i flussi video
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  };

  // Scansione del QR code
  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video o canvas non inizializzati.');
      return;
    }

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext('2d');

    const scan = () => {
      if (!isScanning) return;

      try {
        // Disegna il frame video sul canvas
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  
        // Ottieni i dati dell'immagine dal canvas
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  
        // Decodifica il QR code
        const qrCode = jsQR(imageData.data, imageData.width, imageData.height);
        console.log('QR Code trovato:', qrCode.data);
        if (qrCode) {
          console.log('QR Code trovato:', qrCode.data);
          handleQRCode(qrCode.data); // Gestisci il QR code trovato
          stopScanner(); // Ferma la scansione dopo aver trovato un QR code
          return;
        }
      } catch (error) {
        console.error('Errore durante la scansione del QR code:', error);
      }

      // Continua a scansionare
      requestAnimationFrame(scan);
    };

    scan();
  };

  // Gestione del QR code
  const handleQRCode = (data) => {
    try {
      const id = data; // Supponiamo che il QR code contenga direttamente l'ID
      const photo = photos.find((p) => p.id === parseInt(id, 10));

      if (photo) {
        navigateTo(`photo/${id}`); // Naviga alla pagina dell'immagine
      } else {
        alert('Nessuna immagine trovata per questo QR code.');
      }
    } catch (error) {
      console.error('Errore durante la gestione del QR code:', error);
      alert('QR code non valido.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 text-amber-100">
      <h2 className="text-xl font-bold mb-4 text-center text-amber-300">Scansiona QR Code</h2>

      <div className="bg-stone-800 rounded-lg overflow-hidden shadow-md p-4 mb-6">
        <p className="text-sm mb-4">
          Inquadra un QR code presente nel locale per scoprire la storia di quel particolare angolo del pub.
        </p>

        {isScanning ? (
          <div className="relative mb-4">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-auto rounded"
            />
            <canvas ref={canvasRef} className="hidden" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="border-2 border-green-500 w-64 h-64 flex items-center justify-center">
                <div className="border border-green-500 w-56 h-56"></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-stone-900 rounded-lg h-64 flex items-center justify-center mb-4">
            <QrCode size={64} className="text-stone-700" />
          </div>
        )}

        <button
          onClick={toggleScanner}
          className={`w-full py-3 ${
            isScanning
              ? 'bg-red-700 hover:bg-red-600'
              : 'bg-green-700 hover:bg-green-600'
          } text-white rounded-lg shadow transition-colors flex items-center justify-center`}
        >
          <QrCode size={20} className="mr-2" />
          {isScanning ? 'Interrompi scansione' : 'Inizia scansione'}
        </button>
      </div>

      {errorMessage && (
        <div className="bg-red-700 text-white p-4 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}

      <div className="bg-stone-800 rounded-lg p-4">
        <h3 className="font-bold text-amber-300 mb-2">Come funziona</h3>
        <ol className="list-decimal pl-5 text-sm space-y-2">
          <li>Trova un QR code all'interno del Celtic Druid</li>
          <li>Premi "Inizia scansione" e inquadra il codice</li>
          <li>Visualizza la foto storica associata a quel punto del locale</li>
          <li>Crea il tuo ricordo "prima e dopo" per confrontare passato e presente</li>
        </ol>
      </div>
    </div>
  );
};

export default QrScanner;